import { DealerProfileSettingsActionTypes as AT } from './constants';
import {
  getBranchesWithProfileSettings,
  getAllToggledProfileServicesForDealer,
  getUpdateOneProfileServiceForDealer,
} from './selectors';
import { SERVICE_CATEGORIES_NAMES } from './ServicesPanel/services/names/index.names';

/**
 * @typedef {import('./ServicesPanel/services/names/index.names').DealerProfileServiceCategoryKey} DealerProfileServiceCategoryKey
 * @typedef {import('./ServicesPanel/services/names/index.names').AllServicesNames} AllServicesNames
 * @typedef {[key: string]: Record<DealerProfileServiceCategoryKey, AllServicesNames[]>} BranchProfileSettingsDictionary
 * @typedef {import('./ServicesPanel/services/index.name').DealerProfileServiceCategoryValue} DealerProfileServiceCategoryValue
 */

/**
 * @typedef {Object} DealerProfileSettingsState
 * @property {BranchProfileSettingsDictionary} branchProfileSettingsDictionary         - dictionary with all dealer's current profile settings
 * @property {string}  dealerIdBeingEdited                                             - dealer id of the user currently editing the profile
 * @property {string}  nextSelectedDealerId                                            - dealer id that user wishes to select, but cannot until changes to settings are saved/discarded
 * @property {boolean} dealerProfileSettingsApplyChangesToAllSelected                  - true if user has selected checkbox to apply selected changes to all other accessible dealers in network
 * @property {boolean} dealerProfileSettingsFormHasBeenEdited                          - true if form for current dealer has been touch by the user
 * @property {boolean} dealerProfileSettingsSubmitDisabled                             - true if submission button should be disabled
 * @property {any}     dealerProfileSettingsSubmitError                                - state if there is a submission error
 * @property {boolean} dealerProfileSettingsSubmitInProgress                           - true when submission is in progress
 * @property {boolean} dealerProfileSettingsSubmittedSuccessfully                      - true if a submission has completed
 * @property {boolean} saveChangesModalIsVisible                                       - true if the save changes modal is open
 * @property {boolean} showConfirmationAnimationVisible                                - true if show confirmation animation is visible after modal submission
 * @property {DealerProfileServiceCategoryValue} selectedDealerProfileSettingsCategory - currently selected settings category in UI
 */

/**
 * @description
 * dealerProfileSettings initial state
 *
 * @returns {DealerProfileSettingsState}
 */
export const defaultState = () => ({
  branchProfileSettingsDictionary: {},
  dealerIdBeingEdited: '',
  nextSelectedDealerId: '',
  dealerProfileSettingsSubmitInProgress: false,
  dealerProfileSettingsSubmitError: null,
  dealerProfileSettingsSubmitDisabled: true,
  dealerProfileSettingsSubmittedSuccessfully: false,
  dealerProfileSettingsApplyChangesToAllSelected: false,
  dealerProfileSettingsFormHasBeenEdited: false,
  saveChangesModalIsVisible: false,
  showConfirmationAnimationVisible: false,
  selectedDealerProfileSettingsCategory:
    SERVICE_CATEGORIES_NAMES.GENERAL_SERVICES,
});

/**
 * @description
 * dealerProfileSettings reducer
 *
 * @param {DealerProfileSettingsState} state
 * @param {{type: string, payload?: object}} action
 *
 * @returns {DealerProfileSettingsState}
 */
export const dealerProfileSettings = (state = defaultState(), action) => {
  switch (action.type) {
    case AT.RESET_ALL_SUBMISSION_STATES:
      return {
        ...state,
        dealerProfileSettingsSubmitInProgress: false,
        dealerProfileSettingsSubmitError: null,
        dealerProfileSettingsSubmitDisabled: true,
        dealerProfileSettingsSubmittedSuccessfully: false,
        dealerProfileSettingsFormHasBeenEdited: false,
      };
    case AT.SET_DEALER_ID_BEING_EDITED:
      return {
        ...state,
        dealerIdBeingEdited: action.payload.dealerId,
      };
    case AT.SET_SELECTED_DEALER_PROFILE_SETTINGS_CATEGORY_TAB:
      return {
        ...state,
        selectedDealerProfileSettingsCategory:
          action.payload.selectedDealerProfileSettingsCategory,
      };
    case AT.SET_INITIAL_DEALER_PROFILE_SETTINGS_OPTIONS:
      return {
        ...state,
        branchProfileSettingsDictionary: getBranchesWithProfileSettings({
          branchesDictionary: action.payload.branchesDictionary,
        }),
      };
    case AT.SET_SAVE_CHANGES_MODAL_IS_VISIBLE:
      return {
        ...state,
        saveChangesModalIsVisible: action.payload.modalIsVisible,
        dealerProfileSettingsSubmitError: null,
        nextSelectedDealerId:
          action.payload.nextSelectedDealerId || state.nextSelectedDealerId,
      };
    case AT.SET_SHOW_CONFIRMATION_ANIMATION:
      return {
        ...state,
        showConfirmationAnimationVisible:
          action.payload.showConfirmationAnimation,
      };
    case AT.SUBMIT_DEALER_PROFILE_SETTINGS_BEGIN:
      return {
        ...state,
        dealerProfileSettingsSubmitInProgress: true,
        dealerProfileSettingsSubmitError: null,
      };
    case AT.SUBMIT_DEALER_PROFILE_SETTINGS_FAIL:
      return {
        ...state,
        dealerProfileSettingsSubmitInProgress: false,
        dealerProfileSettingsSubmitDisabled: true,
        dealerProfileSettingsSubmitError: action.payload.error,
      };
    case AT.SUBMIT_DEALER_PROFILE_SETTINGS_SUCCESS:
      return {
        ...state,
        dealerProfileSettingsSubmitInProgress: false,
        dealerProfileSettingsSubmitDisabled: true,
        dealerProfileSettingsSubmittedSuccessfully: true,
        dealerProfileSettingsApplyChangesToAllSelected: false,
        dealerProfileSettingsFormHasBeenEdited: false,
      };

    case AT.TOGGLE_ALL_DEALER_PROFILE_SETTINGS_OPTIONS:
      return {
        ...state,
        dealerProfileSettingsSubmitDisabled: false,
        dealerProfileSettingsSubmittedSuccessfully: false,
        dealerProfileSettingsFormHasBeenEdited: true,
        branchProfileSettingsDictionary: getAllToggledProfileServicesForDealer({
          dealerId: action.payload.dealerId,
          branchProfileSettingsDictionary:
            state.branchProfileSettingsDictionary,
        }),
      };
    case AT.TOGGLE_APPLY_CHANGES_TO_ALL:
      return {
        ...state,
        dealerProfileSettingsApplyChangesToAllSelected: !state.dealerProfileSettingsApplyChangesToAllSelected,
        dealerProfileSettingsSubmitDisabled: false,
        dealerProfileSettingsFormHasBeenEdited: true,
      };
    case AT.UPDATE_PROFILE_SETTING_FOR_DEALER:
      const {
        payload: { dealerId, serviceName, debugLabel },
      } = action;
      const { selectedDealerProfileSettingsCategory } = state;

      // 'debugLabel' can be removed before merging
      console.log({ debugLabel });
      return {
        ...state,
        dealerProfileSettingsSubmitDisabled: false,
        dealerProfileSettingsSubmittedSuccessfully: false,
        dealerProfileSettingsFormHasBeenEdited: true,
        branchProfileSettingsDictionary: getUpdateOneProfileServiceForDealer({
          dealerId,
          serviceName,
          selectedDealerProfileSettingsCategory,
          branchProfileSettingsDictionary:
            state.branchProfileSettingsDictionary,
        }),
      };
    default:
      return state;
  }
};
